import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { EscopoContext } from './context'
import {
    aplicarFaceta,
    arr,
    type Escopo,
    type FacetaId,
    limparEscopo,
    montarResumoVisual,
    proximaAtiva,
    type Registro,
    type ValorFaceta,
} from './escopo'
import { useFacetaOptions } from './useFacetaOptions'

type Props = {
    value: Escopo
    onChange: (proximo: Escopo) => void
    children: React.ReactNode
}

/**
 * Headless: mantém o escopo, o registro das facetas (é ele que sabe se uma
 * faceta é single ou múltipla) e o estado de abertura do shell.
 */
export function Root({ value, onChange, children }: Props) {
    const [registro, setRegistro] = useState<Registro[]>([])
    const [ativa, setAtiva] = useState<FacetaId | null>(null)
    const [aberto, setAberto] = useState(false)
    const [painel, setPainel] = useState<HTMLElement | null>(null)
    const [busca, setBusca] = useState('')

    const dados = useFacetaOptions(value, aberto, registro, busca)

    const registrar = useCallback((novo: Registro) => {
        setRegistro(anterior =>
            anterior.some(item => item.id === novo.id)
                ? anterior
                : [...anterior, novo],
        )
        return () =>
            setRegistro(anterior =>
                anterior.filter(item => item.id !== novo.id),
            )
    }, [])

    useEffect(() => {
        if (!registro.length) return
        setAtiva(proximaAtiva(registro, ativa))
    }, [registro, ativa])

    // trocar de faceta ou fechar o painel zera a busca: ela é da faceta ativa
    useEffect(() => setBusca(''), [ativa, aberto])

    const setFaceta = useCallback(
        (id: FacetaId, valor: ValorFaceta) =>
            onChange(aplicarFaceta(value, registro, id, valor)),
        [value, registro, onChange],
    )

    const limpar = useCallback(() => {
        onChange(limparEscopo(registro))
        setBusca('')
    }, [registro, onChange, setBusca])

    const nomesEmpresas = useMemo(() => {
        const escolhidas = arr(value.empresas)
        return dados.empresas.opcoes
            .filter(opcao => escolhidas.includes(opcao.id))
            .map(opcao => opcao.label)
    }, [value.empresas, dados.empresas.opcoes])

    const resumoVisual = useMemo(
        () =>
            montarResumoVisual({
                escopo: value,
                registro,
                nomesEmpresas,
                nomeFuncionario: dados.funcionarioSelecionado?.label,
            }),
        [value, registro, nomesEmpresas, dados.funcionarioSelecionado],
    )

    const toggleDemitidos = useCallback(
        (incluir: boolean) => onChange({ ...value, incluirDemitidos: incluir }),
        [value, onChange],
    )

    return (
        <EscopoContext.Provider
            value={{
                valor: value,
                setFaceta,
                toggleDemitidos,
                limpar,
                registrar,
                registro,
                ativa,
                setAtiva,
                aberto,
                setAberto,
                busca,
                setBusca,
                dados,
                resumoVisual,
                painel,
                setPainel,
            }}
        >
            <div className="gap-1 flex flex-row">{children}</div>
        </EscopoContext.Provider>
    )
}
